import { useEffect, useState } from 'react'
import { Receipt } from 'lucide-react'
import { Link } from '../router.js'
import { apiGet } from '../admin/primitives.js'
import { formatMoney } from '../finance/money.js'
import { useSoiree } from './SoireeData.js'

/**
 * BILLETTERIE DE LA NUIT — VALEUR FACIALE, ET RIEN D'AUTRE.
 *
 * ═══ CE QUE CE CHIFFRE EST, AU MOT PRÈS ═══
 *
 * C'est la somme des prix AFFICHÉS sur les billets valides de la nuit. Vérifié
 * sur la reddition Shotgun R-588427-001 (soirée du 29/08/2026) : Gestion rend
 * 4 664,00 €, c'est-à-dire EXACTEMENT la ligne « Revenus (TTC) » du
 * fournisseur — 197 billets en ligne (1 534,00 €) plus 418 sur place
 * (3 130,00 €).
 *
 * Ce n'est donc NI le « Public TTC » payé par le client (4 977,23 € ce
 * soir-là), NI le solde effectivement versé par le fournisseur (1 731,00 €).
 * Les frais, les remises commerciales, la TVA et le solde N'EXISTENT PAS dans
 * les données de Gestion : ils ne sont ni affichés, ni déduits, ni approchés.
 * D'où le vocabulaire de cette tuile — « valeur faciale », jamais « chiffre
 * d'affaires », jamais « encaissement ».
 *
 * ═══ LE MONTANT NE VIENT PAS SI LE DROIT N'EST PAS LÀ ═══
 *
 * `soiree.read` n'accorde pas `finance.live.read`. La tuile ne se protège pas
 * en se cachant : sans ce droit, le serveur renvoie `ticketing: null` et aucun
 * euro ne quitte la machine. Cacher un composant qui a déjà reçu les montants
 * n'aurait été qu'un décor.
 *
 * ═══ LA NUIT EST CALCULÉE PAR LE SERVEUR ═══
 *
 * On envoie l'identifiant de la soirée, pas une date : `/api/v1/soirees/:id`
 * ne porte pas la nuit commerciale, et la recalculer ici aurait ajouté une
 * cinquième copie de la coupure de 8 h — quatre existent déjà et donnent le
 * même résultat en 2026, ce qui rendrait une divergence invisible.
 */

interface Billetterie {
  available: boolean
  online_cents: number
  onsite_cents: number
  tickets: number
  free_tickets: number
  refunded_tickets: number
  caveats: string[]
}

interface Rapprochement {
  facial_cents: number | null
  closed_cents: number | null
  /** Ventes du guichet d'une autre nuit rattachées par décision : la faciale « par soirée » en est corrigée. */
  off_night_cents?: number
  gap_cents: number | null
  overlap: boolean
  reserves: string[]
}

interface NuitBilletterie {
  business_date: string
  ticketing: Billetterie | null
  onsite_overlap: Rapprochement | null
}

/**
 * `apiGet` résout aussi sur un 403 ou un 404 : la réponse est alors le corps
 * d'erreur, pas la donnée. Sans ce contrôle, l'écran afficherait un panneau
 * vide au lieu de dire ce qui s'est passé.
 */
function estReponse(v: unknown): v is NuitBilletterie {
  return typeof v === 'object' && v !== null && typeof (v as NuitBilletterie).business_date === 'string'
}

type Etat = { statut: 'chargement' } | { statut: 'refus' } | { statut: 'ok'; d: NuitBilletterie }

export function SoireeFinanceWidget() {
  const { id } = useSoiree()
  const [etat, setEtat] = useState<Etat>({ statut: 'chargement' })

  useEffect(() => {
    if (!id) return
    let vivant = true
    void apiGet<unknown>(`/api/v1/finance/revenue/night-ticketing?soiree=${encodeURIComponent(id)}`)
      .then((r) => { if (vivant) setEtat(estReponse(r) ? { statut: 'ok', d: r } : { statut: 'refus' }) })
      .catch(() => { if (vivant) setEtat({ statut: 'refus' }) })
    return () => { vivant = false }
  }, [id])

  const b = etat.statut === 'ok' ? etat.d.ticketing : null
  // On ne compose le total QUE lorsque la valeur faciale est affirmable : un
  // « 0,00 € » posé là aurait exactement l'allure d'un chiffre juste.
  const faciale = b !== null && b.available ? b.online_cents + b.onsite_cents : null

  return (
    <section className="lc-panel gd-panel so-widget" aria-label="Billetterie — valeur faciale">
      <header className="lc-panel__head">
        <h2 className="lc-section-title">
          <Receipt size={16} aria-hidden="true" /> Billetterie — valeur faciale
        </h2>
        <span className="lc-hint">Provisoire</span>
      </header>

      <div className="lc-panel__body">
        {etat.statut === 'chargement' ? <p className="lc-hint">Chargement…</p> : null}

        {etat.statut === 'refus' ? (
          <p className="gd-calm">
            Ces montants ne sont pas accessibles depuis ce compte. Ce refus porte
            sur la donnée elle-même : aucun euro n'a été transmis au navigateur.
          </p>
        ) : null}

        {etat.statut === 'ok' && b === null ? (
          <p className="gd-calm">
            Aucun montant n'a été transmis : le droit « billetterie en direct »
            n'est pas accordé à ce compte.
          </p>
        ) : null}

        {b !== null ? (
          <>
            <dl className="lc-kv">
              <div>
                <dt>Valeur faciale</dt>
                <dd>{faciale === null ? '—' : formatMoney(faciale)}</dd>
              </div>
              <div>
                <dt>Billets valides</dt>
                <dd>{b.tickets.toLocaleString('fr-FR')}</dd>
              </div>
              <div>
                <dt>Préventes et en ligne</dt>
                <dd>{b.available ? formatMoney(b.online_cents) : '—'}</dd>
              </div>
              <div>
                <dt>Au guichet (sur place)</dt>
                <dd>{b.available ? formatMoney(b.onsite_cents) : '—'}</dd>
              </div>
            </dl>

            <div className="so-minitiles">
              <span className="so-minitile">
                <strong>{b.free_tickets.toLocaleString('fr-FR')}</strong><span>à 0 €</span>
              </span>
              {b.refunded_tickets > 0 ? (
                <span className="so-minitile so-minitile--warn">
                  <strong>{b.refunded_tickets.toLocaleString('fr-FR')}</strong><span>remboursés, non déduits</span>
                </span>
              ) : null}
            </div>

            {/* Le rapprochement du guichet : deux mesures d'un même
                encaissement, montrées côte à côte pour qu'on cesse de les
                additionner de tête. Jamais de total. */}
            {etat.statut === 'ok' && etat.d.onsite_overlap?.overlap ? (
              <dl className="lc-kv">
                <div>
                  <dt>Caisse d'entrée arrêtée</dt>
                  <dd>{formatMoney(etat.d.onsite_overlap.closed_cents)}</dd>
                </div>
                {(etat.d.onsite_overlap.off_night_cents ?? 0) !== 0 ? (
                  <div>
                    <dt>Ventes d'une autre nuit, rattachées</dt>
                    <dd>{formatMoney(etat.d.onsite_overlap.off_night_cents!)}</dd>
                  </div>
                ) : null}
                <div>
                  <dt>Écart avec le guichet</dt>
                  <dd>{formatMoney(etat.d.onsite_overlap.gap_cents)}</dd>
                </div>
              </dl>
            ) : null}

            <p className="lc-hint">
              Chiffre PROVISOIRE : la valeur faciale est la somme des prix
              affichés sur les billets. Les frais du fournisseur, les remises
              commerciales, la TVA et le solde reversé n'existent pas dans cette
              donnée — ils ne sont ni affichés ni déduits. Seule la clôture de
              la nuit arrête une recette.
            </p>

            {[...b.caveats, ...(etat.statut === 'ok' ? etat.d.onsite_overlap?.reserves ?? [] : [])].map((c) => (
              <p className="lc-hint" key={c}>{c}</p>
            ))}
          </>
        ) : null}

        <p className="lc-hint">
          <Link href="/comptabilite/chiffre-affaires">Voir dans Compta</Link>
        </p>
      </div>
    </section>
  )
}
